import { createFirebaseSync } from './createFirebaseSync.js';
import { createLocalSync } from './createLocalSync.js';

export {
  GROUP_SESSION_TTL_MS,
  groupProgress,
  isSessionJoinable,
  liveVoteCounts,
  placeSummary,
  readGroupState,
  restaurantFromPlace,
} from './state.js';

/**
 * The contract both sync backends implement. `useGroupSession` only talks to
 * this interface, so swapping in a websocket or Supabase adapter later means
 * adding one file here and nothing else.
 *
 * @typedef {object} GroupSync
 * @property {string}   code
 * @property {boolean}  isRealtime  False for the single-machine fallback.
 * @property {() => Promise<GroupState | null>} snapshot  Current state, or null if the session was never created.
 * @property {(member: {memberId: string, name: string}) => Promise<void>} join
 * @property {(guests: {id: string, name: string}[]) => Promise<void>} invite  Names the people this session is for.
 * @property {(selection: GroupDeckSelection | null) => Promise<void>} setDeck  Stores the host's selected deck.
 * @property {(onState: (state: GroupState) => void) => (() => void)} subscribe
 * @property {(vote: {memberId: string, restaurantId: number, direction: 'left'|'right', time?: string|null, place?: PlaceSummary|null}) => Promise<void>} recordVote
 * @property {(member: {memberId: string}) => Promise<void>} submit  Marks this member's votes as submitted.
 * @property {(member: {memberId: string}) => Promise<void>} end  Closes voting for everyone. Idempotent.
 * @property {(member: {memberId: string}) => Promise<void>} leave
 */

/**
 * @typedef {object} GroupState
 * @property {Record<string, {name: string, joinedAt: number}>} members
 * @property {Record<string, Record<string, 'left'|'right'>>}   votes
 * @property {Record<string, Record<string, string>>}           times
 * @property {GroupDeckSelection | null}                        deck  Host-selected deck, or null for the normal algorithmic deck.
 * @property {Record<string, {name: string}>}                   invites  People this session was opened for, by member key. Empty for a group started from scratch.
 * @property {Record<string, number>}                           submitted  Members who finished swiping their deck.
 * @property {{at: number, by: string} | null}                  ended  Set once everyone has submitted or the host ends the group.
 * @property {Record<string, PlaceSummary>}                     places  Enough of each right-swiped restaurant to show it in results.
 */

/**
 * @typedef {Pick<import('../../../lib/types.js').Restaurant,
 *   'id'|'name'|'cuisine'|'neighborhood'|'price'|'rating'|'imageSeed'|'coords'|'reservationUrl'|'instagramUrl'>} PlaceSummary
 */

/**
 * Picks the best available sync backend for a session code.
 *
 * Prefers Firebase so invited phones can join, and silently degrades to
 * cross-tab localStorage when it isn't configured.
 *
 * @param {string} code
 * @returns {Promise<GroupSync>}
 */
export async function createGroupSync(code) {
  return (await createFirebaseSync(code)) ?? createLocalSync(code);
}

/**
 * Reads a session without joining it.
 *
 * The invite screen has a question to answer before anybody is a member —
 * whether this session is expecting particular people, and so whether to offer
 * a list of names instead of an empty name field.
 *
 * @param {string} code
 * @returns {Promise<GroupState | null>}
 */
export async function peekSession(code) {
  return (await createGroupSync(code)).snapshot();
}

/**
 * Raised when a join can't proceed. `reason` is `'not-found'` when nothing was
 * ever created at the code, or `'expired'` when the session aged past the TTL
 * (or everyone left). The UI turns this into a message.
 */
export class GroupJoinError extends Error {
  /** @param {'not-found' | 'expired'} reason */
  constructor(reason) {
    super(reason === 'not-found' ? 'That group code was not found.' : 'That group has ended.');
    this.name = 'GroupJoinError';
    this.reason = reason;
  }
}
